import { create } from 'zustand';
import { AuthService } from '../services/AuthService';
import type { User } from '@/types/user';

type AuthState = {
  user: User | null;
  isLoading: boolean;
  error: string | null;
};

type AuthActions = {
  register: (email: string, password: string, fullName: string, orgName: string) => Promise<void>;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: (skipRedirect?: boolean) => Promise<void>;
};

const svc = new AuthService();

// Deduplicate in-flight requests to /api/auth/me (e.g., StrictMode double effects)
let ongoingMeRequest: Promise<User> | null = null;

export const useAuthStore = create<AuthState & AuthActions>((set) => ({
  user: null,
  isLoading: false,
  error: null,

  register: async (email, password, fullName, orgName) => {
    try {
      
      set({ isLoading: true, error: null });
      await svc.register({ email, password, fullName });
      // Ensure onboarding redirect happens immediately after first login
      try { localStorage.setItem('ima_redirect_onboarding', '1'); } catch {}
      
      await useAuthStore.getState().login(email, password);
      
      
      // Create organization immediately after login
      try {
        const makeId = () => `org_${Math.random().toString(36).slice(2, 10)}`;
        const slugify = (s: string) => s
          .toLowerCase()
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)+/g, '');
        const id = makeId();
        const body = { id, name: orgName.trim(), slug: slugify(orgName.trim()) || slugify(id) } as any;
        
        
        const res = await fetch('/api/orgs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body)
        });
        
        if (res.ok) {
          
          // Set active org immediately
          try {
            const { useOrgStore } = await import('../stores/orgStore');
            useOrgStore.getState().setActiveOrgId(id);
            await useOrgStore.getState().fetchOrgs();
            
            // For a brand new org, consider onboarding not completed until user finishes flow
            
            try { 
              localStorage.setItem(`ima_onboarding_completed_${id}`, '0');
              
            } catch {}
            try { 
              localStorage.setItem('ima_redirect_onboarding', '1');
              
            } catch {}
          } catch (e) {
            
          }
        } else {
          
        }
      } catch (e) {
        
      }
      // No session flag needed; routing logic forces onboarding first
    } catch (e) {
      
      set({ error: e instanceof Error ? e.message : 'Registration failed' });
    } finally {
      set({ isLoading: false });
    }
  },

  login: async (email, password, rememberMe) => {
    try {
      set({ isLoading: true, error: null });
      const me = await svc.login({ email, password, rememberMe });
      set({ user: me, isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Login failed', isLoading: false });
    }
  },

  logout: async () => {
    try {
      set({ isLoading: true, error: null });
      await svc.logout();

      // Wipe every user-scoped store so stale data never bleeds across accounts
      const [{ useCreditStore }, { useOrgStore }, { usePaywallStore }, { useAppStore }] = await Promise.all([
        import('./creditStore'),
        import('./orgStore'),
        import('./paywallStore'),
        import('./appStore'),
      ]);

      useCreditStore.getState().clear();
      useOrgStore.setState({ orgs: [], activeOrgId: null, isLoading: false, error: null, initialized: false });
      usePaywallStore.setState({ isModalOpen: false, triggerSource: null, paywallVariant: null, isCanvasLocked: false, canvasDismissed: false });
      useAppStore.setState({ galleryImages: [], galleryTotal: 0, galleryPage: 1, galleryHasMore: false, galleryLoadingMore: false, isLoading: false, error: null });

      // Clear org localStorage so the next login doesn't auto-select the old org
      try { localStorage.removeItem('ima_active_org_id'); } catch {}

      // Nuke React Query cache so the next login starts fresh
      const { queryClient } = await import('@/lib/queryClient');
      queryClient.clear();

      set({ user: null, isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Logout failed', isLoading: false });
    }
  },

  fetchMe: async (skipRedirect = false) => {
    // If a request is already in-flight, await it instead of issuing another
    if (ongoingMeRequest) {
      try {
        await ongoingMeRequest;
      } finally {
        set({ isLoading: false });
      }
      return;
    }

    try {
      set({ isLoading: true, error: null });
      ongoingMeRequest = svc.me(skipRedirect);
      const me = await ongoingMeRequest;
      set({ user: me, isLoading: false });
    } catch (e) {
      set({ user: null, isLoading: false });
    } finally {
      ongoingMeRequest = null;
    }
  },
}));


